import React, { useState, useEffect, useContext } from "react";
import { getPendingApprovals, processApproval, getApprovalHistory, updateLineGL, unapproveReport } from "../api";
import { ToastContext } from "../App";

function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val || 0);
}
function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const CATEGORY_ICONS = {
  Meals: "🍽️", Mileage: "🚗", Lodging: "🏨",
  Transportation: "✈️", "Office Supplies": "📦", Other: "🧾",
};

const STATUS_LABELS = {
  Approved: { label: "Approved", cls: "badge-approved" },
  Rejected: { label: "Returned", cls: "badge-rejected" },
};

// GL account options — value stored in SQL is the account number only;
// description is shown in the dropdown for Keith's convenience.
const GL_ACCOUNTS = [
  { number: "500040", description: "Third-Party Consulting" },
  { number: "607010", description: "Travel Expense - Air Transportation" },
  { number: "607020", description: "Travel Expense - Ground Transportation" },
  { number: "607030", description: "Travel Expense - Lodging" },
  { number: "607040", description: "Travel Expense - Meals" },
  { number: "607050", description: "Travel Expense - Other" },
  { number: "609010", description: "Dues & Subscriptions - Software License" },
  { number: "609020", description: "Dues & Subscriptions" },
  { number: "610020", description: "Other Operating - Office Repairs and Maintenance" },
  { number: "610030", description: "Other Operating - Postage and Delivery" },
  { number: "610040", description: "Other Operating - Office Supplies" },
  { number: "610050", description: "Other Operating - Office Expense" },
  { number: "610070", description: "Other Operating - Meals & Entertainment" },
  { number: "610100", description: "Other Operating - Trainings and Seminars" },
  { number: "604050", description: "Events and Conferences" },
];

export default function ApprovalView() {
  const showToast = useContext(ToastContext);
  const [reports, setReports]       = useState([]);
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [processing, setProcessing] = useState(null);
  const [rejectReason, setRejectReason]   = useState({});
  const [showReject, setShowReject]       = useState({});
  const [lineAcctNums, setLineAcctNums]  = useState({});
  // Shape: { [reportId]: { [lineId]: string } }
  const setLineAcct = (reportId, lineId, val) =>
    setLineAcctNums((prev) => ({
      ...prev,
      [reportId]: { ...(prev[reportId] || {}), [lineId]: val },
    }));
  const [historySearch, setHistorySearch]   = useState("");
  const [dateFrom, setDateFrom]             = useState("");
  const [dateTo, setDateTo]                 = useState("");
  const [expandedHistory, setExpandedHistory] = useState({});
  const [editingGL, setEditingGL]           = useState(null); // { lineId, reportId }
  const [editingGLVal, setEditingGLVal]     = useState("");
  const [savingGL, setSavingGL]             = useState(false);

  const load = () => {
    setLoading(true);
    getPendingApprovals()
      .then(setReports)
      .catch(() => showToast("Failed to load approvals", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  const loadHistory = (from, to) => {
    setHistoryLoading(true);
    getApprovalHistory({ from, to })
      .then(setHistory)
      .catch(() => showToast("Failed to load history", "error"))
      .finally(() => setHistoryLoading(false));
  };

  const toggleHistory = () => {
    if (!showHistory && history.length === 0) loadHistory(dateFrom, dateTo);
    setShowHistory((v) => !v);
  };

  // Reload history whenever dates change (if history panel is open)
  useEffect(() => {
    if (showHistory) loadHistory(dateFrom, dateTo);
  }, [dateFrom, dateTo]); // eslint-disable-line

  const handleSaveGL = async (lineId, reportId) => {
    if (!editingGLVal) return;
    try {
      setSavingGL(true);
      await updateLineGL(lineId, editingGLVal);
      setHistory((prev) =>
        prev.map((r) =>
          r.ID !== reportId
            ? r
            : {
                ...r,
                lines: (r.lines || []).map((l) =>
                  l.ID === lineId ? { ...l, AccountNumber: editingGLVal } : l
                ),
              }
        )
      );
      setEditingGL(null);
      showToast("GL account updated", "success");
    } catch (e) {
      showToast(e.message || "Failed to update GL account", "error");
    } finally {
      setSavingGL(false);
    }
  };

  const [unapproving, setUnapproving] = useState(null);

  const handleUnapprove = async (reportId) => {
    if (!window.confirm("Move this report back to pending approvals for re-approval?")) return;
    try {
      setUnapproving(reportId);
      await unapproveReport(reportId);
      setHistory((prev) => prev.filter((r) => r.ID !== reportId));
      showToast("Report moved back to pending approvals", "success");
    } catch (e) {
      showToast(e.message || "Failed to unapprove report", "error");
    } finally {
      setUnapproving(null);
    }
  };

  const handleAction = async (reportId, action) => {
    const reason = rejectReason[reportId] || "";
    if (action === "reject" && !reason.trim()) {
      showToast("Please enter a reason for rejection", "error");
      return;
    }
    if (action === "approve") {
      const report = reports.find((r) => r.ID === reportId);
      const lines  = report?.lines || [];
      const accts  = lineAcctNums[reportId] || {};
      if (lines.some((l) => !accts[l.ID]?.trim())) {
        showToast("Please select an account for every line item before approving", "error");
        return;
      }
    }
    try {
      setProcessing(reportId);
      await processApproval(reportId, action, reason, lineAcctNums[reportId] || {});
      showToast(
        action === "approve" ? "Report approved!" : "Report returned to employee",
        action === "approve" ? "success" : "default"
      );
      setReports((prev) => prev.filter((r) => r.ID !== reportId));
      if (showHistory) loadHistory();
    } catch (e) {
      showToast(e.message || "Action failed", "error");
    } finally {
      setProcessing(null);
    }
  };

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div>
      {/* Pending queue */}
      {reports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎉</div>
          <div className="empty-state-title">All caught up!</div>
          <div className="empty-state-sub">No expense reports waiting for approval.</div>
        </div>
      ) : (
        <>
          <div className="divider">{reports.length} Report{reports.length !== 1 ? "s" : ""} Pending</div>

          {reports.map((report) => {
            const lines = report.lines || [];
            const total = lines.reduce((s, l) => s + (Number(l.Amount) || 0), 0);
            const busy  = processing === report.ID;
            const displayName = report.OnBehalfOfName || report.EmployeeName;
            const isOnBehalf  = !!report.OnBehalfOfName;

            return (
              <div key={report.ID} className="approval-card">
                {/* Header */}
                <div className="approval-card-header">
                  <div className="approval-card-name">{displayName}</div>
                  <div className="approval-card-meta">
                    {isOnBehalf
                      ? <>via {report.EmployeeName} ({report.EmployeeEmail}) · Submitted {formatDate(report.SubmittedDate)}</>
                      : <>{report.EmployeeEmail} · Submitted {formatDate(report.SubmittedDate)}</>
                    }
                  </div>
                  {report.ReportPurpose && (
                    <div style={{ marginTop: 4, fontSize: 13, color: "var(--gray-5)", fontStyle: "italic" }}>
                      📋 {report.ReportPurpose}
                    </div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: "var(--blue)" }}>
                    {formatCurrency(total)}
                  </div>
                </div>

                {/* Line items */}
                {lines.map((line) => (
                  <React.Fragment key={line.ID}>
                    <div className="approval-line">
                      <div className="approval-line-left">
                        <div className="approval-line-cat">
                          {CATEGORY_ICONS[line.Category] || "🧾"} {line.Category}
                        </div>
                        <div className="approval-line-desc">
                          {line.Description || "—"}
                          {line.Category === "Mileage" && line.Miles ? ` · ${line.Miles} mi` : ""}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--gray-4)", marginTop: 2 }}>
                          {formatDate(line.ExpenseDate)}
                        </div>
                      </div>
                      <div className="approval-line-right">
                        <div className="approval-line-amt">{formatCurrency(line.Amount)}</div>
                        {line.ReceiptUrl && (
                          <a
                            href={line.ReceiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 12, color: "var(--blue)" }}
                          >
                            📎 Receipt
                          </a>
                        )}
                      </div>
                    </div>
                    {/* Per-line GL account dropdown */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 16px 10px" }}>
                      <span style={{ fontSize: 12, color: "var(--gray-4)", whiteSpace: "nowrap" }}>
                        GL Acct <span style={{ color: "var(--red)" }}>*</span>
                      </span>
                      <select
                        value={(lineAcctNums[report.ID] || {})[line.ID] || ""}
                        onChange={(e) => setLineAcct(report.ID, line.ID, e.target.value)}
                        style={{
                          flex: 1,
                          padding: "7px 12px",
                          borderRadius: 8,
                          border: "1.5px solid var(--gray-2)",
                          fontSize: 13,
                          outline: "none",
                          background: "var(--gray-1)",
                          color: (lineAcctNums[report.ID] || {})[line.ID] ? "var(--gray-6)" : "var(--gray-4)",
                          cursor: "pointer",
                        }}
                      >
                        <option value="">Select account…</option>
                        {GL_ACCOUNTS.map((acct) => (
                          <option key={acct.number} value={acct.number}>
                            {acct.number} — {acct.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  </React.Fragment>
                ))}

                {/* Reject reason input */}
                {showReject[report.ID] && (
                  <div style={{ padding: "8px 16px", borderTop: "1px solid var(--gray-2)" }}>
                    <div className="rejection-input">
                      <input
                        placeholder="Reason for returning…"
                        value={rejectReason[report.ID] || ""}
                        onChange={(e) =>
                          setRejectReason((prev) => ({ ...prev, [report.ID]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAction(report.ID, "reject");
                        }}
                        autoFocus
                      />
                      <button
                        className="btn btn-danger"
                        style={{ width: "auto", padding: "10px 16px", fontSize: 13 }}
                        onClick={() => handleAction(report.ID, "reject")}
                        disabled={busy}
                      >
                        {busy ? "…" : "Return"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="approval-actions">
                  <button
                    className="btn btn-success"
                    onClick={() => handleAction(report.ID, "approve")}
                    disabled={busy}
                  >
                    {busy ? "…" : "✓ Approve"}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() =>
                      setShowReject((prev) => ({ ...prev, [report.ID]: !prev[report.ID] }))
                    }
                    disabled={busy}
                  >
                    ↩ Return
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* History toggle */}
      <div style={{ marginTop: 24, textAlign: "center" }}>
        <button
          className="btn btn-ghost"
          style={{ fontSize: 13, padding: "8px 20px" }}
          onClick={toggleHistory}
        >
          {showHistory ? "▲ Hide History" : "▼ View Approval History"}
        </button>
      </div>

      {/* History list */}
      {showHistory && (
        <div style={{ marginTop: 12 }}>
          {historyLoading ? (
            <div className="loader"><div className="spinner" /></div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--gray-4)", padding: "24px 0", fontSize: 14 }}>
              No history yet.
            </div>
          ) : (
            <>
              {/* Search bar */}
              <div style={{ padding: "0 0 8px 0" }}>
                <input
                  type="text"
                  placeholder="Search by name, status, or date…"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1.5px solid var(--gray-2)",
                    fontSize: 14,
                    outline: "none",
                    background: "var(--gray-1)",
                    color: "var(--gray-6)",
                  }}
                />
              </div>

              {/* Date range filter */}
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "0 0 12px 0" }}>
                <label style={{ fontSize: 13, color: "var(--gray-4)", whiteSpace: "nowrap" }}>From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    flex: 1,
                    boxSizing: "border-box",
                    padding: "9px 12px",
                    borderRadius: 10,
                    border: "1.5px solid var(--gray-2)",
                    fontSize: 14,
                    outline: "none",
                    background: "var(--gray-1)",
                    color: "var(--gray-6)",
                  }}
                />
                <label style={{ fontSize: 13, color: "var(--gray-4)", whiteSpace: "nowrap" }}>To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    flex: 1,
                    boxSizing: "border-box",
                    padding: "9px 12px",
                    borderRadius: 10,
                    border: "1.5px solid var(--gray-2)",
                    fontSize: 14,
                    outline: "none",
                    background: "var(--gray-1)",
                    color: "var(--gray-6)",
                  }}
                />
                {(dateFrom || dateTo) && (
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: "9px 12px", whiteSpace: "nowrap" }}
                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>

              {(() => {
                const q = historySearch.trim().toLowerCase();
                const filtered = history.filter((r) => {
                  const displayName = r.OnBehalfOfName || r.EmployeeName;
                  const textMatch = !q || (
                    displayName.toLowerCase().includes(q) ||
                    (r.OnBehalfOfName  || "").toLowerCase().includes(q) ||
                    (r.EmployeeName    || "").toLowerCase().includes(q) ||
                    (r.EmployeeEmail   || "").toLowerCase().includes(q) ||
                    (r.ReportStatus    || "").toLowerCase().includes(q) ||
                    (r.ApproverName    || "").toLowerCase().includes(q) ||
                    formatDate(r.ApprovedDate).toLowerCase().includes(q) ||
                    (r.RejectionReason || "").toLowerCase().includes(q) ||
                    (r.lines || []).some((l) => (l.AccountNumber || "").toLowerCase().includes(q))
                  );
                  const dateStr   = r.ApprovedDate ? r.ApprovedDate.slice(0, 10) : "";
                  const fromMatch = !dateFrom || dateStr >= dateFrom;
                  const toMatch   = !dateTo   || dateStr <= dateTo;
                  return textMatch && fromMatch && toMatch;
                });

                if (filtered.length === 0) {
                  return (
                    <div style={{ textAlign: "center", color: "var(--gray-4)", padding: "16px 0", fontSize: 14 }}>
                      No results match your search or date range.
                    </div>
                  );
                }

                return (
                  <>
                    <div className="divider">
                      {(q || dateFrom || dateTo)
                        ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""}`
                        : "Recent Decisions"}
                    </div>
                    {filtered.map((r) => {
                      const st          = STATUS_LABELS[r.ReportStatus] || { label: r.ReportStatus, cls: "" };
                      const lines       = r.lines || [];
                      const total       = lines.reduce((s, l) => s + (Number(l.Amount) || 0), 0);
                      const expanded    = !!expandedHistory[r.ID];
                      const hasLines    = lines.length > 0;
                      const displayName = r.OnBehalfOfName || r.EmployeeName;
                      const isOnBehalf  = !!r.OnBehalfOfName;
                      return (
                        <div key={r.ID} className="report-card" style={{ cursor: hasLines ? "pointer" : "default" }}
                          onClick={() => hasLines && setExpandedHistory((prev) => ({ ...prev, [r.ID]: !prev[r.ID] }))}
                        >
                          <div className="report-card-header">
                            <div style={{ flex: 1 }}>
                              <span className={`badge ${st.cls}`}>{st.label}</span>
                              <div className="report-card-date" style={{ fontWeight: 600, marginTop: 4 }}>
                                {displayName}
                              </div>
                              {isOnBehalf && (
                                <div style={{ fontSize: 11, color: "var(--gray-4)", marginTop: 1 }}>
                                  via {r.EmployeeName}
                                </div>
                              )}
                              <div className="report-card-date" style={{ marginTop: 2 }}>
                                {r.ApproverName} · {formatDate(r.ApprovedDate)}
                              </div>
                              {r.ReportPurpose && (
                                <div style={{ fontSize: 12, color: "var(--gray-4)", marginTop: 3, fontStyle: "italic" }}>
                                  📋 {r.ReportPurpose}
                                </div>
                              )}
                              {r.ReportStatus === "Rejected" && r.RejectionReason && (
                                <div style={{ fontSize: 12, color: "var(--red)", marginTop: 4 }}>
                                  "{r.RejectionReason}"
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div className="report-card-total">{formatCurrency(total)}</div>
                              <div className="report-card-lines">
                                {lines.length} line{lines.length !== 1 ? "s" : ""}
                              </div>
                              {hasLines && (
                                <div style={{ fontSize: 12, color: "var(--blue)", marginTop: 4 }}>
                                  {expanded ? "▲ Hide" : "▼ View"}
                                </div>
                              )}
                              {r.ReportStatus === "Approved" && (
                                <button
                                  className="btn btn-ghost"
                                  style={{ fontSize: 11, padding: "4px 10px", marginTop: 8, color: "var(--orange, #e07b00)", borderColor: "var(--orange, #e07b00)" }}
                                  disabled={unapproving === r.ID}
                                  onClick={(e) => { e.stopPropagation(); handleUnapprove(r.ID); }}
                                >
                                  {unapproving === r.ID ? "…" : "↩ Unapprove"}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Expanded line items */}
                          {expanded && (
                            <div onClick={(e) => e.stopPropagation()}>
                              {lines.map((line) => (
                                <div key={line.ID} className="approval-line">
                                  <div className="approval-line-left">
                                    <div className="approval-line-cat">
                                      {CATEGORY_ICONS[line.Category] || "🧾"} {line.Category}
                                    </div>
                                    <div className="approval-line-desc">
                                      {line.Description || "—"}
                                      {line.Category === "Mileage" && line.Miles ? ` · ${line.Miles} mi` : ""}
                                    </div>
                                    <div style={{ fontSize: 11, color: "var(--gray-4)", marginTop: 2 }}>
                                      {formatDate(line.ExpenseDate)}
                                    </div>
                                    <div style={{ fontSize: 11, color: "var(--gray-5)", marginTop: 3 }}>
                                      {editingGL?.lineId === line.ID ? (
                                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 2 }}>
                                          <select
                                            value={editingGLVal}
                                            onChange={(e) => setEditingGLVal(e.target.value)}
                                            style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1.5px solid var(--gray-2)", background: "var(--gray-1)", outline: "none" }}
                                          >
                                            <option value="">Select…</option>
                                            {GL_ACCOUNTS.map((acct) => (
                                              <option key={acct.number} value={acct.number}>
                                                {acct.number} — {acct.description}
                                              </option>
                                            ))}
                                          </select>
                                          <button
                                            className="btn btn-success"
                                            style={{ fontSize: 11, padding: "3px 10px" }}
                                            onClick={() => handleSaveGL(line.ID, r.ID)}
                                            disabled={savingGL || !editingGLVal}
                                          >
                                            {savingGL ? "…" : "Save"}
                                          </button>
                                          <button
                                            className="btn btn-ghost"
                                            style={{ fontSize: 11, padding: "3px 10px" }}
                                            onClick={() => setEditingGL(null)}
                                            disabled={savingGL}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <span style={{ fontWeight: 600 }}>
                                          GL: {line.AccountNumber || "—"}
                                          {" "}
                                          <button
                                            onClick={() => { setEditingGL({ lineId: line.ID, reportId: r.ID }); setEditingGLVal(line.AccountNumber || ""); }}
                                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--blue)", padding: 0, verticalAlign: "middle" }}
                                            title="Edit GL account"
                                          >
                                            ✏️
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="approval-line-right">
                                    <div className="approval-line-amt">{formatCurrency(line.Amount)}</div>
                                    {line.ReceiptUrl && (
                                      <a
                                        href={line.ReceiptUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 12, color: "var(--blue)" }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        📎 Receipt
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
}
